/**
 * Shows a dashboard the analyst just composed, inside the conversation.
 *
 * The specification and a small preview arrive on the agent stream, so the
 * panels paint straight away. The authoritative rows are then fetched from the
 * API, which is also what refresh and export use — one data path rather than
 * three.
 *
 * A preview that already holds the whole result set is not re-fetched: the
 * composer says so per panel, and running the same governed query twice to
 * learn nothing new costs the user engine time.
 */
import { AlertTriangle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiError, dashboardsApi } from "../services/api";
import type { DashboardComposedEvent } from "../services/agui";
import type { Dashboard, PanelResult } from "../types";
import { DashboardView } from "./DashboardView";

interface ComposedDashboardProps {
  event: DashboardComposedEvent;
  onDismiss: () => void;
  onOpenWorkspace: () => void;
}

/**
 * Turns the streamed previews into panel results.
 *
 * Panels are matched by index because the preview is produced before the
 * server assigns panel ids, and the API preserves panel order when it saves.
 */
function previewResults(
  event: DashboardComposedEvent,
  dashboard: Dashboard,
): PanelResult[] {
  const results: PanelResult[] = [];
  for (const preview of event.spec.previews ?? []) {
    const panel = dashboard.panels[preview.panelIndex];
    if (!panel || preview.rows.length === 0) continue;
    const columns = [
      ...new Set(preview.rows.flatMap((row) => Object.keys(row))),
    ];
    results.push({
      panelId: panel.id,
      status: "ok",
      columns,
      rows: preview.rows,
      rowCount: preview.rowCount,
      truncated: !preview.complete,
      units: preview.units,
      endorsement: preview.endorsement ?? null,
      periodNotes: preview.periodNotes ?? [],
      provenance: null,
      error: null,
    });
  }
  return results;
}

export function ComposedDashboard({
  event,
  onDismiss,
  onOpenWorkspace,
}: ComposedDashboardProps) {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [seeded, setSeeded] = useState<PanelResult[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!event.saved || !event.dashboardId) return;
    let cancelled = false;

    // The saved dashboard is the source of truth: it carries the server's
    // panel ids and layouts, which the streamed specification does not.
    dashboardsApi
      .get(event.dashboardId)
      .then((saved) => {
        if (cancelled) return;
        setDashboard(saved);
        setSeeded(previewResults(event, saved));
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof ApiError
            ? cause.message
            : "The saved dashboard could not be opened.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [event]);

  if (!event.saved) {
    // The charts were composed but nothing was persisted. Offering to open a
    // workspace entry that does not exist would be worse than saying so.
    return (
      <div className="inline-notice warning" role="alert">
        <AlertTriangle size={17} aria-hidden="true" />
        <span>
          {event.saveError ??
            "The dashboard was composed but could not be saved to your workspace."}{" "}
          Ask again to retry.
        </span>
        <button type="button" className="button tertiary" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inline-notice warning" role="alert">
        <AlertTriangle size={17} aria-hidden="true" />
        <span>{error}</span>
        <button
          type="button"
          className="button tertiary"
          onClick={onOpenWorkspace}
        >
          Open workspace
        </button>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="panel-skeleton" role="status" aria-live="polite">
        <span className="sr-only">Opening your dashboard…</span>
        <i />
        <i />
        <i />
      </div>
    );
  }

  return (
    <div className="composed-dashboard">
      <button
        type="button"
        className="icon-button composed-dismiss"
        onClick={onDismiss}
        aria-label="Hide this dashboard"
      >
        <X size={17} />
      </button>
      <DashboardView
        dashboard={dashboard}
        initialResults={seeded}
        onRenamed={setDashboard}
      />
    </div>
  );
}
